import React, { useState } from "react";
import Timeline from "./Timeline.jsx";
import PositionsPane from "./PositionsPane.jsx";
import GlobePane from "./GlobePane.jsx";
import PartiesPane from "./PartiesPane.jsx";
import ActivityTicker from "./ActivityTicker.jsx";
import useActivityStream from "./useActivityStream.js";
import {
  useGlobeData,
  useTimelineEvents,
  useCounterparties,
  useFxWindows,
  usePositions,
  useActivityPolled,
} from "./useTerminalOverview.js";
import { MOCK_ACTIVITY } from "./mockData.js";

/* Composed Overview tab — three columns over a streaming activity
   ticker, settlement timeline pinned above. Data comes in via props
   so the parent can switch from MOCK_* to real APIs (A.1–A.7) one
   at a time. */

export default function OverviewScreen({
  token,
  wsUrl = process.env.REACT_APP_ACTIVITY_WS,  // §A.7 stream URL
}) {
  const [activeSym, setActiveSym] = useState(null);

  /* Live data — every pane is its own hook so a partially-rolled-out
     backend degrades pane-by-pane. */
  const { positions }                   = usePositions(token);
  const { events: timelineEvents }      = useTimelineEvents(token);
  const { parties }                     = useCounterparties(token);
  const { windows: fxWindows }          = useFxWindows(token);
  const { countries, countryVolume, corridors } = useGlobeData(token);

  /* Activity: prefer WebSocket if configured, otherwise poll the
     legacy transactions endpoint via useActivityPolled. */
  const polled = useActivityPolled(wsUrl ? null : token);
  const streamed = useActivityStream({
    wsUrl: token ? wsUrl : null,
    token,
    initialEvents: MOCK_ACTIVITY,
  });
  const activityEvents = wsUrl ? streamed.events : polled.events;
  const streamStatus   = wsUrl ? streamed.status : (polled.status === "live" ? "streaming" : polled.status === "loading" ? "loading" : "fallback");

  return (
    <>
      <Timeline events={timelineEvents} />
      <div
        style={{
          position:"relative", zIndex:1,
          display:"grid",
          gridTemplateColumns:"380px 1fr 380px",
          gridTemplateRows:"1fr 220px",
          gridTemplateAreas: '"positions globe parties" "activity activity activity"',
          height:"calc(100vh - 40px - 30px - 92px)",
          minHeight:0,
        }}
      >
        <div style={{ gridArea:"positions", display:"flex", minHeight:0 }}>
          <PositionsPane positions={positions} activeSym={activeSym} onActiveSymChange={setActiveSym} />
        </div>
        <div style={{ gridArea:"globe", display:"flex", minHeight:0 }}>
          <GlobePane countries={countries} countryVolume={countryVolume} corridors={corridors} />
        </div>
        <div style={{ gridArea:"parties", display:"flex", minHeight:0 }}>
          <PartiesPane parties={parties} fxWindows={fxWindows} />
        </div>
        <div style={{ gridArea:"activity", display:"flex", minHeight:0 }}>
          <ActivityTicker events={activityEvents} status={streamStatus} />
        </div>
      </div>
    </>
  );
}
